/**
 * Shrink an ops evidence photo in the browser before it is uploaded.
 *
 * The upload endpoint (`POST /v1/shipments/uploads/main`) sits behind a 5 MB
 * multipart cap and accepts JPEG / PNG / WebP only; a modern phone photo is
 * often larger, and an oversized upload comes back as a 500 rather than a clear
 * message. So: files that already fit and are an accepted type go through
 * untouched; anything else is decoded, scaled to at most MAX_EDGE on its long
 * side and re-encoded as JPEG.
 */

export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const MAX_EDGE = 2560;
const QUALITIES = [0.85, 0.7];
const ACCEPTED = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** A decoded image that can re-encode itself at a given size as JPEG. */
export interface DecodedImage {
  width: number;
  height: number;
  toJpeg: (width: number, height: number, quality: number) => Promise<Blob>;
  close?: () => void;
}

export type ImageDecoder = (file: File) => Promise<DecodedImage>;

/** A photo that cannot be used; `message` is shown to ops under the field. */
export class EvidenceImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EvidenceImageError';
  }
}

const isHeic = (file: File): boolean =>
  /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);

/** Browser decoder: createImageBitmap (honours EXIF orientation) + canvas. */
export const browserDecoder: ImageDecoder = async (file) => {
  const bitmap = await createImageBitmap(file);
  return {
    width: bitmap.width,
    height: bitmap.height,
    close: () => bitmap.close(),
    toJpeg: (width, height, quality) =>
      new Promise<Blob>((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('canvas unavailable'));
          return;
        }
        ctx.drawImage(bitmap, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
          'image/jpeg',
          quality
        );
      }),
  };
};

const scaled = (width: number, height: number) => {
  const longSide = Math.max(width, height);
  if (longSide <= MAX_EDGE) return { width, height };
  const ratio = MAX_EDGE / longSide;
  return {
    width: Math.round(width * ratio),
    height: Math.round(height * ratio),
  };
};

const jpegName = (name: string) => name.replace(/\.[^./\\]+$/, '') + '.jpg';

/**
 * Return a File that the upload endpoint accepts, or throw an
 * EvidenceImageError with the message ops should see.
 */
export const prepareEvidenceFile = async (
  file: File,
  decode: ImageDecoder = browserDecoder
): Promise<File> => {
  if (ACCEPTED.has(file.type) && file.size <= MAX_UPLOAD_BYTES) return file;

  let image: DecodedImage;
  try {
    image = await decode(file);
  } catch {
    if (isHeic(file)) {
      throw new EvidenceImageError(
        'Format HEIC belum didukung, ubah ke JPG lalu unggah lagi.'
      );
    }
    throw new EvidenceImageError(
      'Foto tidak bisa dibaca. Gunakan JPG, PNG, atau WebP.'
    );
  }

  try {
    const { width, height } = scaled(image.width, image.height);
    for (const quality of QUALITIES) {
      const blob = await image.toJpeg(width, height, quality);
      if (blob.size <= MAX_UPLOAD_BYTES) {
        return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
      }
    }
  } catch {
    // Fall through to the size message below.
  } finally {
    image.close?.();
  }
  throw new EvidenceImageError(
    'Foto terlalu besar (maksimal 5 MB) dan gagal dikecilkan. Coba foto lain.'
  );
};
